/**
 * Apply for Marriage Certificate Page
 * CRUD operations for certificate applications
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../api/apiClient";
import { usePageTitle } from "../contexts/PageTitleContext";
import { certificateService } from "../services/api/certificate.service";
import { serviceChargeService } from "../services/api/serviceCharge.service";
import { workflowService } from "../services/api/workflow.service";
import { notificationService } from "../services/api/notification.service";
import type { ServiceCharge } from "../types/serviceCharge.types";
import type {
  CertificateApplication,
  CreateCertificateApplicationDto,
  County,
} from "../types/certificate.types";
import {
  WorkflowId,
  WorkflowModule,
  type Workflow,
} from "../types/workflow.types";
import { UserStatusId } from "../types/user.types";
import {
  ApplicationCategory,
  CountyEnum,
  CountyLabels,
} from "../types/certificate.types";
import BusyOverlay from "../components/common/BusyOverlay";
import {
  ApplicationPermissionId,
  HasPermission,
  ModuleId,
} from "../utils/permissions";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { showSuccessToast, showConfirm } from "../utils/toast";
import { REQUIRED_FIELD_MESSAGE } from "../utils/constants";
import "../styles/pages/Users.css";

export const WeddingApply: React.FC = () => {
  const fallbackCounties: County[] = Object.entries(CountyLabels)
    .map(([id, name]) => ({ id: Number(id), name }))
    .filter((county) => Number.isFinite(county.id))
    .sort((a, b) => a.id - b.id);

  const normalizeCounties = (payload: unknown): County[] => {
    const source = payload as
      | County[]
      | { data?: County[]; items?: County[]; $values?: County[] }
      | undefined;

    const rows = Array.isArray(source)
      ? source
      : Array.isArray(source?.data)
        ? source.data
        : Array.isArray(source?.items)
          ? source.items
          : Array.isArray(source?.$values)
            ? source.$values
            : [];

    return rows
      .map((county) => ({
        id: Number(county?.id),
        name: String(county?.name ?? "").trim(),
      }))
      .filter((county) => Number.isFinite(county.id) && county.name.length > 0)
      .sort((a, b) => a.id - b.id);
  };

  // Service charge state
  const [serviceCharge, setServiceCharge] = useState<ServiceCharge | null>(
    null,
  );

  // Add application form data
  const [addFormData, setAddFormData] =
    useState<CreateCertificateApplicationDto>({
      category: ApplicationCategory.Marriage,
      email: "",
      placeOfWedding: "",
      dateOfWedding: "",
      countyId: 0,
      processAtHeadquarters: false,
      workflowId: WorkflowId.CountyMarriagesWorkflow,
    });

  // Define missing state variables
  const [addError, setAddError] = useState<string>("");
  const [addLoading, setAddLoading] = useState<boolean>(false);
  const [addValidationErrors, setAddValidationErrors] = useState<
    Record<string, string>
  >({});

  // Fetch service price for WeddingCertificate (serviceCategoryId = 1)
  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const weddingPrice =
          await serviceChargeService.getServiceChargeByCategoryId(1);

        setServiceCharge(weddingPrice);
      } catch {
        setServiceCharge(null);
      }
    };
    fetchPrice();
  }, []);
  const { setPageTitle } = usePageTitle();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [applications, setApplications] = useState<CertificateApplication[]>(
    [],
  );
  const [counties, setCounties] = useState<County[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [countyFilter, setCountyFilter] = useState<number | "all">("all");
  const [statusFilter, setStatusFilter] = useState<number | "all">(
    UserStatusId.Created,
  );

  const [showAddModal, setShowAddModal] = useState(false);
  const [isRoleCountyLinked, setIsRoleCountyLinked] = useState(false);
  const [linkedCountyId, setLinkedCountyId] = useState<number | null>(null);

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle(
      "Apply for Marriage Certificate",
      "Submit and manage marriage certificate applications",
    );
    fetchData();
    fetchCounties();
    fetchWorkflows(
      addFormData.processAtHeadquarters
        ? WorkflowModule.HQMarriages
        : WorkflowModule.CountyMarriages,
    );
  }, [setPageTitle]);

  // Fetch workflows when processAtHeadquarters changes
  useEffect(() => {
    fetchWorkflows(
      addFormData.processAtHeadquarters
        ? WorkflowModule.HQMarriages
        : WorkflowModule.CountyMarriages,
    );
  }, [addFormData.processAtHeadquarters]);

  const checkRoleCountyLink = async () => {
    try {
      const response = await apiRequest("/RoleCounties/is-linked", {
        method: "GET",
      });

      if (!response.ok) {
        setIsRoleCountyLinked(false);
        setLinkedCountyId(null);
        return;
      }

      const payload = (await response.json().catch(() => null)) as {
        countyId?: number | null;
        data?: { countyId?: number | null };
      } | null;

      const source = payload?.data ?? payload;
      const countyCandidate = Number(source?.countyId);

      if (Number.isFinite(countyCandidate) && countyCandidate > 0) {
        setIsRoleCountyLinked(true);
        setLinkedCountyId(countyCandidate);
        setAddFormData((prev) => ({
          ...prev,
          processAtHeadquarters: false,
          countyId: countyCandidate,
          workflowId: WorkflowId.CountyMarriagesWorkflow,
        }));
        return;
      }

      setIsRoleCountyLinked(false);
      setLinkedCountyId(null);
    } catch {
      setIsRoleCountyLinked(false);
      setLinkedCountyId(null);
    }
  };

  useEffect(() => {
    checkRoleCountyLink();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");
      const data =
        await certificateService.getCertificateApplicationsByCategory(
          ApplicationCategory.Marriage,
        );
      setApplications(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error("Error fetching applications:", err);
      setError(err?.message || "Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const fetchCounties = async () => {
    try {
      const data = await certificateService.getCounties();
      const normalizedCounties = normalizeCounties(data);
      setCounties(
        normalizedCounties.length > 0 ? normalizedCounties : fallbackCounties,
      );
    } catch (err: any) {
      console.error("Error fetching counties:", err);
      setCounties(fallbackCounties);
    }
  };

  const fetchWorkflows = async (
    moduleId: number = WorkflowModule.CountyMarriages,
  ) => {
    try {
      const data = await workflowService.getWorkflowsByModule(moduleId);
      setWorkflows(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error("Error fetching workflows:", err);
    }
  };

  // Auto-select workflow based on processAtHeadquarters
  useEffect(() => {
    const targetWorkflowId = addFormData.processAtHeadquarters
      ? WorkflowId.HQMarriagesWorkflow
      : WorkflowId.CountyMarriagesWorkflow;

    if (addFormData.workflowId !== targetWorkflowId) {
      setAddFormData((prev) => ({
        ...prev,
        workflowId: targetWorkflowId,
      }));
    }
  }, [addFormData.processAtHeadquarters, addFormData.workflowId]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError("");
    const errors: Record<string, string> = {};

    if (!addFormData.countyId) {
      errors.countyId = REQUIRED_FIELD_MESSAGE;
    }
    if (!addFormData.placeOfWedding.trim()) {
      errors.placeOfWedding = REQUIRED_FIELD_MESSAGE;
    }
    if (!addFormData.dateOfWedding) {
      errors.dateOfWedding = REQUIRED_FIELD_MESSAGE;
    }

    setAddValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Submit Application",
      "Are you sure you want to submit this marriage certificate application? You can edit it after submission if needed.",
      "Yes, submit",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setAddLoading(true);
      const createPayload = {
        ...addFormData,
        ...(serviceCharge
          ? {
              lraPayment: serviceCharge.lraPayment,
              operationalCost: serviceCharge.operationalCost,
              currencyId: serviceCharge.currencyId,
            }
          : {}),
      };
      const created =
        await certificateService.createCertificateApplication(createPayload);
      setShowAddModal(false);
      resetAddForm();
      await fetchData();
      showSuccessToast("Application submitted successfully");
      // Send email notification if settings allow
      if (created.workflowId && created.id) {
        notificationService
          .sendEmailNotification(created.workflowId, created.id)
          .catch(() => {});
      }
    } catch (err: any) {
      setAddError(err?.message || "Failed to submit application");
    } finally {
      setAddLoading(false);
    }
  };

  const resetAddForm = () => {
    setAddFormData({
      category: ApplicationCategory.Marriage,
      email: "",
      placeOfWedding: "",
      dateOfWedding: "",
      countyId: isRoleCountyLinked && linkedCountyId ? linkedCountyId : 0,
      processAtHeadquarters: isRoleCountyLinked ? false : false,
      workflowId: WorkflowId.CountyMarriagesWorkflow,
    });
    setAddError("");
    setAddValidationErrors({});
  };

  const filteredApplications = applications.filter((app) => {
    const term = searchTerm.trim().toLowerCase();
    const marriageDateText = app.dateOfWedding
      ? new Date(app.dateOfWedding).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "2-digit",
        })
      : "";

    const searchableValues = [
      app.formNo,
      app.groomName,
      app.brideName,
      app.countyName,
      app.email,
      app.placeOfWedding,
      marriageDateText,
      app.status?.name,
      app.statusName,
    ];

    const matchesSearch =
      term.length === 0 ||
      searchableValues.some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(term),
      );

    const matchesCounty =
      countyFilter === "all" || app.countyId === countyFilter;

    const matchesStatus =
      statusFilter === "all" || app.statusId === statusFilter;

    return matchesSearch && matchesCounty && matchesStatus;
  });

  // Get unique statuses that exist in the applications
  const availableStatuses = Array.from(
    new Set(
      applications
        .filter((app) => app.statusId !== undefined)
        .map((app) => ({
          id: app.statusId!,
          name: app.statusName || "Unknown",
        })),
    ),
  ).reduce(
    (acc, status) => {
      if (!acc.find((s) => s.id === status.id)) {
        acc.push(status);
      }
      return acc;
    },
    [] as { id: number; name: string }[],
  );

  if (!HasPermission(ModuleId.Weddings, ApplicationPermissionId.Apply)) {
    return (
      <div
        className={`dashboard-body ${
          sidebarOpen ? "sidebar-open" : "sidebar-closed"
        }`}
      >
        <Topbar
          onMenuToggle={() => setSidebarOpen((prev) => !prev)}
          sidebarOpen={sidebarOpen}
        />
        <Sidebar isOpen={sidebarOpen} />
        <main className="main-content">
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span>
              You do not have permission to apply for marriage certificates
            </span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Topbar
        onMenuToggle={() => setSidebarOpen((prev) => !prev)}
        sidebarOpen={sidebarOpen}
      />
      <Sidebar isOpen={sidebarOpen} />
      <main className="main-content">
        <div className="users-page">
          <BusyOverlay
            isVisible={loading || addLoading}
            message={
              addLoading
                ? "Submitting application..."
                : "Loading applications..."
            }
          />

          <div className="users-content">
            <div className="users-header">
              <input
                type="text"
                className="search-input"
                placeholder="Search by Form No, Groom, Bride, County, Email, Place of Marriage, or Date of Marriage..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <select
                className="filter-select"
                value={countyFilter}
                onChange={(e) =>
                  setCountyFilter(
                    e.target.value === "all"
                      ? "all"
                      : parseInt(e.target.value, 10),
                  )
                }
                style={{
                  padding: "0.5rem 1rem",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  fontSize: "0.875rem",
                  minWidth: "150px",
                }}
              >
                <option value="all">All Counties</option>
                {counties.map((county) => (
                  <option key={county.id} value={county.id}>
                    {county.name}
                  </option>
                ))}
              </select>
              <select
                className="filter-select"
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(
                    e.target.value === "all"
                      ? "all"
                      : parseInt(e.target.value, 10),
                  )
                }
                style={{
                  padding: "0.5rem 1rem",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  fontSize: "0.875rem",
                  minWidth: "150px",
                }}
              >
                <option value="all">All Statuses</option>
                {availableStatuses.map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </select>
              <button
                className="btn btn--primary"
                onClick={() => setShowAddModal(true)}
              >
                <span>➕</span> New Application
              </button>
            </div>

            {error && (
              <div className="alert alert-error">
                <span className="alert-icon">⚠️</span>
                <span>{error}</span>
                <button onClick={() => setError("")} className="alert-close">
                  ✕
                </button>
              </div>
            )}

            <div className="users-table-container">
              {filteredApplications.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state__icon">📋</div>
                  <p className="empty-state__text">
                    {searchTerm ||
                    countyFilter !== "all" ||
                    statusFilter !== "all"
                      ? "No applications found matching your criteria"
                      : "No applications yet"}
                  </p>
                </div>
              ) : (
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Form No</th>
                      <th>Groom</th>
                      <th>Bride</th>
                      <th>County</th>
                      <th>Email</th>
                      <th>Place of Marriage</th>
                      <th>Date of Marriage</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApplications.map((app) => (
                      <tr key={app.id}>
                        <td>{app.formNo}</td>
                        <td>{app.groomName || "-"}</td>
                        <td>{app.brideName || "-"}</td>
                        <td>{app.countyName || "-"}</td>
                        <td>{app.email || "-"}</td>
                        <td>{app.placeOfWedding || "-"}</td>
                        <td>
                          {app.dateOfWedding
                            ? new Date(app.dateOfWedding).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "2-digit",
                                },
                              )
                            : "-"}
                        </td>
                        <td>
                          <span className="status-badge status-badge--active">
                            {app.status?.name || app.statusName || "Pending"}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button
                              className="btn-icon"
                              onClick={() =>
                                navigate(`/marriages/apply/${app.id}`)
                              }
                              title="View Detail"
                            >
                              👁️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Add Application Modal */}
          {showAddModal && (
            <div className="modal-overlay modal-overlay--side-panel">
              <div className="modal-content modal-content--side-panel">
                <div className="modal-header">
                  <h2 className="modal-title">New Marriage Application</h2>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={() => {
                      setShowAddModal(false);
                      resetAddForm();
                    }}
                    disabled={addLoading}
                  >
                    ✕
                  </button>
                </div>

                <form className="modal-form" onSubmit={handleCreate}>
                  {addError && (
                    <div className="alert alert-error">
                      <span className="alert-icon">⚠️</span>
                      <span>{addError}</span>
                      <button
                        type="button"
                        onClick={() => setAddError("")}
                        className="alert-close"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  <div className="form-grid-responsive">
                    <div className="switch-container">
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={addFormData.processAtHeadquarters || false}
                          disabled={isRoleCountyLinked}
                          onChange={(e) =>
                            setAddFormData({
                              ...addFormData,
                              processAtHeadquarters: e.target.checked,
                              countyId: e.target.checked
                                ? CountyEnum.CentralOffice
                                : 0,
                            })
                          }
                        />
                        <span className="switch-slider"></span>
                      </label>
                      <span className="switch-label">
                        Process at Headquarters
                      </span>
                    </div>

                    <div className="form-group has-value">
                      <select
                        className="form-input"
                        value={addFormData.workflowId || ""}
                        onChange={(e) =>
                          setAddFormData({
                            ...addFormData,
                            workflowId: e.target.value
                              ? parseInt(e.target.value, 10)
                              : undefined,
                          })
                        }
                        disabled
                        style={{
                          background: "#f1f1f1",
                          cursor: "not-allowed",
                        }}
                      >
                        {workflows.map((workflow) => (
                          <option key={workflow.id} value={workflow.id}>
                            {workflow.name}
                          </option>
                        ))}
                      </select>
                      <label className="form-label">Workflow</label>
                    </div>

                    <div className="form-group has-value">
                      <select
                        className="form-input"
                        value={addFormData.category}
                        onChange={(e) =>
                          setAddFormData({
                            ...addFormData,
                            category: e.target.value,
                          })
                        }
                        disabled
                        required
                        style={{
                          background: "#f1f1f1",
                          cursor: "not-allowed",
                        }}
                      >
                        <option value={ApplicationCategory.Marriage}>
                          {ApplicationCategory.Marriage}
                        </option>
                        <option value={ApplicationCategory.Divorce}>
                          {ApplicationCategory.Divorce}
                        </option>
                      </select>
                      <label className="form-label">
                        Application Category <span className="required">*</span>
                      </label>
                    </div>

                    <div className="form-group has-value">
                      <select
                        className={`form-input ${addValidationErrors.countyId ? "form-input--error" : ""}`}
                        value={addFormData.countyId || ""}
                        onChange={(e) => {
                          setAddFormData({
                            ...addFormData,
                            countyId: parseInt(e.target.value, 10),
                          });
                          if (addValidationErrors.countyId) {
                            setAddValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.countyId;
                              return newErrors;
                            });
                          }
                        }}
                        required
                        disabled={
                          addFormData.processAtHeadquarters ||
                          isRoleCountyLinked
                        }
                        style={
                          addFormData.processAtHeadquarters ||
                          isRoleCountyLinked
                            ? { background: "#f1f1f1", cursor: "not-allowed" }
                            : {}
                        }
                      >
                        <option value="">Select County</option>
                        {addFormData.processAtHeadquarters
                          ? counties
                              .filter(
                                (county) =>
                                  county.id === CountyEnum.CentralOffice,
                              )
                              .map((county) => (
                                <option key={county.id} value={county.id}>
                                  {county.name}
                                </option>
                              ))
                          : counties.map((county) => (
                              <option key={county.id} value={county.id}>
                                {county.name}
                              </option>
                            ))}
                      </select>
                      <label className="form-label">
                        County <span className="required">*</span>
                      </label>
                      {addValidationErrors.countyId && (
                        <div className="field-error">
                          {addValidationErrors.countyId}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 3-Column Row for Payment Fields */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "1rem",
                      width: "100%",
                      marginBottom: "1rem",
                    }}
                  >
                    {/* Readonly Currency field */}
                    <div
                      className={`form-group ${serviceCharge?.currencyName ? "has-value" : ""}`}
                    >
                      <input
                        type="text"
                        className="form-input"
                        value={serviceCharge?.currencyName ?? ""}
                        readOnly
                        placeholder=" "
                        style={{ background: "#f1f1f1", cursor: "not-allowed" }}
                      />
                      <label className="form-label">Currency</label>
                    </div>

                    {/* Readonly LRA Payment field */}
                    {serviceCharge?.lraPayment != null && (
                      <div
                        className={`form-group ${serviceCharge?.lraPayment != null ? "has-value" : ""}`}
                      >
                        <input
                          type="number"
                          className="form-input"
                          value={serviceCharge?.lraPayment ?? ""}
                          readOnly
                          placeholder=" "
                          style={{
                            background: "#f1f1f1",
                            cursor: "not-allowed",
                          }}
                        />
                        <label className="form-label">LRA Payment</label>
                      </div>
                    )}

                    {/* Readonly Operational Cost field */}
                    {serviceCharge?.operationalCost != null && (
                      <div
                        className={`form-group ${serviceCharge?.operationalCost != null ? "has-value" : ""}`}
                      >
                        <input
                          type="number"
                          className="form-input"
                          value={serviceCharge?.operationalCost ?? ""}
                          readOnly
                          placeholder=" "
                          style={{
                            background: "#f1f1f1",
                            cursor: "not-allowed",
                          }}
                        />
                        <label className="form-label">Operational Cost</label>
                      </div>
                    )}
                  </div>

                  {/* 2-Column Row for Date of Marriage and Email */}
                  <div className="form-grid-responsive">
                    <div
                      className={`form-group has-value ${addValidationErrors.dateOfWedding ? "form-group--error" : ""}`}
                    >
                      <input
                        type="date"
                        className={`form-input ${addValidationErrors.dateOfWedding ? "form-input--error" : ""}`}
                        value={addFormData.dateOfWedding}
                        onChange={(e) => {
                          setAddFormData({
                            ...addFormData,
                            dateOfWedding: e.target.value,
                          });
                          if (addValidationErrors.dateOfWedding) {
                            setAddValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.dateOfWedding;
                              return newErrors;
                            });
                          }
                        }}
                        min={new Date().toISOString().split("T")[0]}
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        Date of Marriage <span className="required">*</span>
                      </label>
                      {addValidationErrors.dateOfWedding && (
                        <div className="field-error">
                          {addValidationErrors.dateOfWedding}
                        </div>
                      )}
                    </div>

                    <div
                      className={`form-group ${addFormData.email ? "has-value" : ""}`}
                    >
                      <input
                        type="email"
                        className="form-input"
                        value={addFormData.email}
                        onChange={(e) =>
                          setAddFormData({
                            ...addFormData,
                            email: e.target.value,
                          })
                        }
                        placeholder=" "
                      />
                      <label className="form-label">Email</label>
                    </div>
                  </div>

                  {/* Place of Marriage Field - Full Width */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr",
                      gap: "1rem",
                      width: "100%",
                      marginBottom: "1rem",
                    }}
                  >
                    <div
                      className={`form-group ${addFormData.placeOfWedding ? "has-value" : ""} ${addValidationErrors.placeOfWedding ? "form-group--error" : ""}`}
                    >
                      <input
                        type="text"
                        className={`form-input ${addValidationErrors.placeOfWedding ? "form-input--error" : ""}`}
                        value={addFormData.placeOfWedding}
                        onChange={(e) => {
                          setAddFormData({
                            ...addFormData,
                            placeOfWedding: e.target.value,
                          });
                          if (addValidationErrors.placeOfWedding) {
                            setAddValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.placeOfWedding;
                              return newErrors;
                            });
                          }
                        }}
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        Place of Marriage <span className="required">*</span>
                      </label>
                      {addValidationErrors.placeOfWedding && (
                        <div className="field-error">
                          {addValidationErrors.placeOfWedding}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn--outline"
                      onClick={() => {
                        setShowAddModal(false);
                        resetAddForm();
                      }}
                      disabled={addLoading}
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="btn btn--primary"
                      disabled={addLoading}
                    >
                      {addLoading ? (
                        <>
                          <span className="spinner spinner--sm"></span>
                          Submitting...
                        </>
                      ) : (
                        "Submit Application"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default WeddingApply;
